<template>
  <div class="layout">
    <section class="search-panel">
      <h1>Sklypų paieška</h1>
      <form class="search-form" @submit.prevent="fetchParcel">
        <label for="unikalus">
          <span>Unikalus numeris</span>
          <input
            id="unikalus"
            v-model.trim="form.unikalus_nr"
            type="text"
            placeholder="pvz. 1234-5678-9010"
            autocomplete="off"
          />
        </label>

        <label for="kadastro">
          <span>Kadastro numeris</span>
          <input
            id="kadastro"
            v-model.trim="form.kadastro_nr"
            type="text"
            placeholder="pvz. 1234/0010:0025"
            autocomplete="off"
          />
        </label>

        <div class="form-actions">
          <button class="primary" type="submit" :disabled="loading">
            {{ loading ? 'Ieškoma…' : 'Ieškoti' }}
          </button>
          <button class="ghost" type="button" @click="clearForm" :disabled="loading">
            Išvalyti
          </button>
        </div>
      </form>

      <p class="hint">Įveskite bent vieną lauką ir paspauskite „Ieškoti“.</p>
      <p v-if="error" class="status error">{{ error }}</p>
      <p v-if="info" class="status info">{{ info }}</p>

      <article v-if="parcel" class="parcel-details">
        <h2>Sklypo informacija</h2>
        <dl>
          <div>
            <dt>Unikalus numeris</dt>
            <dd>{{ parcel.unikalus_nr }}</dd>
          </div>
          <div v-if="parcel.kadastro_nr">
            <dt>Kadastro numeris</dt>
            <dd>{{ parcel.kadastro_nr }}</dd>
          </div>
          <div v-if="parcel.sav_pavadinimas">
            <dt>Savivaldybė</dt>
            <dd>{{ parcel.sav_pavadinimas }}</dd>
          </div>
          <div v-if="parcel.seniunijos_pavad">
            <dt>Seniūnija</dt>
            <dd>{{ parcel.seniunijos_pavad }}</dd>
          </div>
          <div v-if="parcel.pask_tipas">
            <dt>Paskirtis</dt>
            <dd>{{ parcel.pask_tipas_pavadinimas || parcel.pask_tipas }}</dd>
          </div>
          <div v-if="parcel.skl_plotas">
            <dt>Plotas</dt>
            <dd>{{ parcel.skl_plotas }}</dd>
          </div>
          <div v-if="parcel.osta_statusas">
            <dt>Statusas</dt>
            <dd>{{ parcel.osta_statusas_pavadinimas || parcel.osta_statusas }}</dd>
          </div>
          <div v-if="parcel.data_rk">
            <dt>Sklypo ribų koregavimo data</dt>
            <dd>{{ formatDate(parcel.data_rk) }}</dd>
          </div>
          <div v-if="parcel.formavimo_data">
            <dt>Formavimo data</dt>
            <dd>{{ formatDate(parcel.formavimo_data) }}</dd>
          </div>
        </dl>
      </article>

      <article v-if="buildings.length" class="building-list">
        <h2>Pastatai sklype</h2>
        <ul class="building-items">
          <li v-for="building in buildings" :key="building.Id">
            <button
              type="button"
              class="building-button"
              :class="{ active: selectedBuildingId === building.Id }"
              @click="selectBuilding(building)"
              :disabled="loading"
            >
              <span class="building-title">
                {{ building.unikalus_nr || 'Nenurodytas numeris' }}
                <span v-if="building.obje_tipas_pavadinimas">
                  · {{ building.obje_tipas_pavadinimas }}
                </span>
              </span>
              <span class="building-meta">
                <span v-if="building.pask_tipas_pavadinimas">
                  {{ building.pask_tipas_pavadinimas }}
                </span>
                <span v-else-if="building.pask_tipas">{{ building.pask_tipas }}</span>
                <span v-if="building.aukstu_skaicius">
                  · Aukštai: {{ building.aukstu_skaicius }}
                </span>
              </span>
            </button>
          </li>
        </ul>

        <section v-if="selectedBuilding" class="building-details">
          <h3>Pasirinktas pastatas</h3>
          <dl>
            <div>
              <dt>Unikalus numeris</dt>
              <dd>{{ selectedBuilding.unikalus_nr }}</dd>
            </div>
            <div v-if="selectedBuilding.obje_tipas_pavadinimas || selectedBuilding.obje_tipas">
              <dt>Objekto tipas</dt>
              <dd>
                {{ selectedBuilding.obje_tipas_pavadinimas || selectedBuilding.obje_tipas }}
              </dd>
            </div>
            <div v-if="selectedBuilding.pask_tipas_pavadinimas || selectedBuilding.pask_tipas">
              <dt>Paskirtis</dt>
              <dd>
                {{ selectedBuilding.pask_tipas_pavadinimas || selectedBuilding.pask_tipas }}
              </dd>
            </div>
            <div v-if="selectedBuilding.osta_statusas_pavadinimas || selectedBuilding.osta_statusas">
              <dt>Statusas</dt>
              <dd>
                {{ selectedBuilding.osta_statusas_pavadinimas || selectedBuilding.osta_statusas }}
              </dd>
            </div>
            <div v-if="selectedBuilding.obuk_bukle_pavadinimas || selectedBuilding.obuk_bukle">
              <dt>Būklė</dt>
              <dd>
                {{ selectedBuilding.obuk_bukle_pavadinimas || selectedBuilding.obuk_bukle }}
              </dd>
            </div>
            <div v-if="selectedBuilding.aukstu_skaicius">
              <dt>Aukštų skaičius</dt>
              <dd>{{ selectedBuilding.aukstu_skaicius }}</dd>
            </div>
            <div v-if="selectedBuilding.statinio_kategorija">
              <dt>Statinio kategorija</dt>
              <dd>{{ selectedBuilding.statinio_kategorija }}</dd>
            </div>
            <div v-if="selectedBuilding.sav_pavadinimas">
              <dt>Savivaldybė</dt>
              <dd>{{ selectedBuilding.sav_pavadinimas }}</dd>
            </div>
            <div v-if="selectedBuilding.seniunijos_pavad">
              <dt>Seniūnija</dt>
              <dd>{{ selectedBuilding.seniunijos_pavad }}</dd>
            </div>
            <div v-if="selectedBuilding.formavimo_data">
              <dt>Formavimo data</dt>
              <dd>{{ formatDate(selectedBuilding.formavimo_data) }}</dd>
            </div>
            <div v-if="selectedBuilding.pts_koregavo_data">
              <dt>PTS koregavo</dt>
              <dd>{{ formatDate(selectedBuilding.pts_koregavo_data) }}</dd>
            </div>
          </dl>
        </section>
      </article>
    </section>

    <section class="map-panel">
      <GoogleMap
        :api-key="apiKey"
        :center="mapCenter"
        :zoom="mapZoom"
        style="width: 100%; height: 100vh"
        map-id="parcel-map"
        @click="handleMapClick"
      >
        <GoogleMapPolygon
          v-for="(polygon, index) in polygonPaths"
          :key="index"
          :options="getPolygonOptions(polygon)"
        />
        <template v-for="building in buildings" :key="`building-${building.Id}`">
          <GoogleMapPolygon
            v-for="(polygon, index) in building.polygons"
            :key="`building-${building.Id}-${index}`"
            :options="getBuildingPolygonOptions(building, polygon)"
          />
        </template>
        <GoogleMapMarker v-if="centroid" :options="{ position: centroid }" />
        <GoogleMapMarker
          v-if="selectedBuilding && selectedBuilding.centroid"
          :options="{ position: selectedBuilding.centroid }"
        />
      </GoogleMap>
      <div v-if="!parcel && !loading" class="map-overlay">Įveskite numerį arba spustelėkite žemėlapyje.</div>
    </section>
  </div>
</template>

<script>
import { computed, reactive, ref } from "vue";
import axios from "axios";

const DEFAULT_CENTER = { lat: 55.169438, lng: 23.881275 }; // Lithuania centroid

export default {
  name: "App",
  setup() {
    const apiKey = "AIzaSyDy9g0EM4e-6YgEvEtGLtnuNvqiUU8sOX4";
    const form = reactive({
      unikalus_nr: "",
      kadastro_nr: "",
    });
    const parcel = ref(null);
    const polygonPaths = ref([]);
    const buildings = ref([]);
    const selectedBuildingId = ref(null);
    const loading = ref(false);
    const error = ref("");
    const info = ref("");
    const mapCenter = ref({ ...DEFAULT_CENTER });
    const mapZoom = ref(7);

    const basePolygonOptions = {
      strokeColor: "#0D47A1",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: "#1976D2",
      fillOpacity: 0.25,
    };

    const baseBuildingPolygonOptions = {
      strokeColor: "#F97316",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: "#FB923C",
      fillOpacity: 0.25,
    };

    const activeBuildingPolygonOptions = {
      strokeColor: "#EA580C",
      strokeWeight: 3,
      fillOpacity: 0.45,
      zIndex: 30,
    };

    const centroid = computed(() => {
      if (!parcel.value) return null;
      const lat = parseFloat(parcel.value.centroid_lat);
      const lng = parseFloat(parcel.value.centroid_lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    });

    const selectedBuilding = computed(() => {
      if (!selectedBuildingId.value) return null;
      return (
        buildings.value.find((building) => building.Id === selectedBuildingId.value) ||
        null
      );
    });

    const clearForm = () => {
      form.unikalus_nr = "";
      form.kadastro_nr = "";
      parcel.value = null;
      polygonPaths.value = [];
      buildings.value = [];
      selectedBuildingId.value = null;
      mapCenter.value = { ...DEFAULT_CENTER };
      mapZoom.value = 7;
      error.value = "";
      info.value = "";
    };

    const formatDate = (value) => {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("lt-LT");
    };

    const buildParams = () => {
      const params = {};
      if (form.unikalus_nr) params.unikalus_nr = form.unikalus_nr;
      if (form.kadastro_nr) params.kadastro_nr = form.kadastro_nr;
      return params;
    };

    const updateMapView = (polygons) => {
      const rings = polygons.flat();

      if (!rings.length) {
        mapCenter.value = { ...DEFAULT_CENTER };
        mapZoom.value = 7;
        return;
      }

      let minLat = 90;
      let maxLat = -90;
      let minLng = 180;
      let maxLng = -180;

      rings.forEach((ring) => {
        ring.forEach(({ lat, lng }) => {
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
          if (lng < minLng) minLng = lng;
          if (lng > maxLng) maxLng = lng;
        });
      });

      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      mapCenter.value = {
        lat: Number.isFinite(centerLat) ? centerLat : DEFAULT_CENTER.lat,
        lng: Number.isFinite(centerLng) ? centerLng : DEFAULT_CENTER.lng,
      };

      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);

      if (maxDiff > 1.5) mapZoom.value = 9;
      else if (maxDiff > 0.5) mapZoom.value = 11;
      else if (maxDiff > 0.1) mapZoom.value = 13;
      else if (maxDiff > 0.03) mapZoom.value = 15;
      else mapZoom.value = 17;
    };

    const normaliseGeoJson = (value) => {
      if (!value) return null;
      if (typeof value === "string") {
        try {
          return JSON.parse(value);
        } catch (error) {
          console.warn("Nepavyko perskaityti GeoJSON:", error);
          return null;
        }
      }
      return value;
    };

    const geoJsonToPaths = (geojson) => {
      if (!geojson || !geojson.type || !geojson.coordinates) return [];

      const ringToLatLng = (ring) => ring.map(([lng, lat]) => ({ lat, lng }));

      if (geojson.type === "Polygon") {
        return [geojson.coordinates.map((ring) => ringToLatLng(ring))];
      }

      if (geojson.type === "MultiPolygon") {
        return geojson.coordinates.map((polygon) =>
          polygon.map((ring) => ringToLatLng(ring))
        );
      }

      return [];
    };

    const transformBuilding = (building) => {
      if (!building) return null;

      const geojson = normaliseGeoJson(building.geojson);
      const polygons = geoJsonToPaths(geojson);

      const lat = parseFloat(building.centroid_lat);
      const lng = parseFloat(building.centroid_lon);
      const centroidValue =
        Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;

      return {
        ...building,
        geojson,
        polygons,
        centroid: centroidValue,
      };
    };

    const loadParcel = async (params, source) => {
      if (!params || !Object.keys(params).length) {
        error.value = "Nurodykite bent vieną numerį.";
        return;
      }

      error.value = "";
      info.value = source === "map" ? "Ieškoma pasirinktoje vietoje…" : "";
      loading.value = true;

      parcel.value = null;
      polygonPaths.value = [];
      buildings.value = [];
      selectedBuildingId.value = null;

      try {
        const { data } = await axios.get("https://varzytiniuzemelapis.lt/wp-json/auctions/v1/parcel", {
          params,
        });

        if (!data || !data.parcel) {
          info.value = data?.message || "Sklypas nerastas.";
          parcel.value = null;
          polygonPaths.value = [];
          buildings.value = [];
          selectedBuildingId.value = null;
          updateMapView([]);
          return;
        }

        parcel.value = data.parcel;

        if (source === "map") {
          form.unikalus_nr = parcel.value.unikalus_nr || "";
          form.kadastro_nr = parcel.value.kadastro_nr || "";
          info.value = "Sklypas rastas pagal žemėlapio paspaudimą.";
        } else {
          info.value = "";
        }

        const geojson = normaliseGeoJson(parcel.value.geojson);
        const polygons = geoJsonToPaths(geojson);
        polygonPaths.value = polygons;
        updateMapView(polygons);

        const parcelBuildings = Array.isArray(parcel.value.buildings)
          ? parcel.value.buildings
          : [];

        buildings.value = parcelBuildings
          .map((building) => transformBuilding(building))
          .filter(Boolean);
      } catch (err) {
        const responseMessage = err?.response?.data?.message;
        error.value = responseMessage || "Įvyko klaida, pabandykite dar kartą.";
      } finally {
        loading.value = false;
      }
    };

    const fetchParcel = async () => {
      const params = buildParams();
      if (!Object.keys(params).length) {
        error.value = "Nurodykite bent vieną numerį.";
        return;
      }

      await loadParcel(params, "form");
    };

    const handleMapClick = async (event) => {
      if (loading.value || !event?.latLng) return;

      const lat = Number(event.latLng.lat().toFixed(8));
      const lng = Number(event.latLng.lng().toFixed(8));

      await loadParcel({ lat, lng }, "map");
    };

    const selectBuilding = (building) => {
      if (!building) return;

      if (selectedBuildingId.value === building.Id) {
        selectedBuildingId.value = null;
        if (polygonPaths.value.length) {
          updateMapView(polygonPaths.value);
        } else {
          updateMapView([]);
        }
        return;
      }

      selectedBuildingId.value = building.Id;

      if (building.polygons && building.polygons.length) {
        updateMapView(building.polygons);
      } else if (building.centroid) {
        mapCenter.value = building.centroid;
        mapZoom.value = 18;
      }
    };

    const getBuildingPolygonOptions = (building, paths) => {
      const isActive = selectedBuildingId.value === building.Id;
      return {
        ...baseBuildingPolygonOptions,
        ...(isActive ? activeBuildingPolygonOptions : {}),
        paths,
      };
    };

    return {
      apiKey,
      form,
      parcel,
      polygonPaths,
      buildings,
      selectedBuildingId,
      selectedBuilding,
      loading,
      error,
      info,
      mapCenter,
      mapZoom,
      centroid,
      fetchParcel,
      handleMapClick,
      selectBuilding,
      clearForm,
      formatDate,
      getPolygonOptions: (paths) => ({
        ...basePolygonOptions,
        paths,
      }),
      getBuildingPolygonOptions,
    };
  },
};
</script>

<style scoped>
.layout {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
}

.search-panel {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  padding: 1.5rem;
}

.search-panel h1 {
  margin: 0 0 1rem;
  font-size: 1.75rem;
}

.search-form {
  display: grid;
  gap: 1rem;
}

.search-form label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 600;
}

.search-form input {
  border-radius: 8px;
  border: 1px solid #d0d7de;
  padding: 0.65rem 0.75rem;
  font-size: 1rem;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
}

button {
  cursor: pointer;
  border-radius: 8px;
  padding: 0.65rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}

button:disabled {
  cursor: default;
  opacity: 0.6;
  transform: none;
  box-shadow: none;
}

button.primary {
  background: #0d47a1;
  color: #ffffff;
  box-shadow: 0 8px 16px rgba(13, 71, 161, 0.25);
}

button.primary:not(:disabled):hover {
  transform: translateY(-1px);
}

button.ghost {
  background: #f1f5f9;
  color: #0f172a;
}

.hint {
  margin: 1rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.status {
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
}

.status.error {
  background: #fee2e2;
  color: #991b1b;
}

.status.info {
  background: #eff6ff;
  color: #1d4ed8;
}

.parcel-details {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.parcel-details h2 {
  margin: 0 0 1rem;
  font-size: 1.3rem;
}

.parcel-details dl {
  display: grid;
  gap: 0.75rem;
}

.parcel-details dt {
  font-weight: 600;
  color: #475569;
}

.parcel-details dd {
  margin: 0.25rem 0 0;
  color: #0f172a;
}

.building-list {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.building-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.building-button {
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  background: #f8fafc;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}

.building-button:hover:not(:disabled) {
  border-color: #cbd5f5;
  background: #eef2ff;
  transform: translateY(-1px);
}

.building-button.active {
  border-color: #1d4ed8;
  background: #dbeafe;
}

.building-button:disabled {
  opacity: 0.7;
  cursor: default;
}

.building-title {
  font-weight: 600;
}

.building-meta {
  color: #475569;
  font-size: 0.9rem;
}

.building-details {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  background: #f8fafc;
}

.building-details h3 {
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
}

.building-details dl {
  display: grid;
  gap: 0.65rem;
}

.building-details dt {
  font-weight: 600;
  color: #475569;
}

.building-details dd {
  margin: 0.2rem 0 0;
  color: #0f172a;
}

.map-panel {
  position: relative;
  height: 60vh;
  min-height: 420px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
}

.map-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.1rem;
  color: #475569;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(241, 245, 249, 0.85), rgba(241, 245, 249, 0.65));
}

@media (min-width: 992px) {
  .layout {
    flex-direction: row;
    align-items: flex-start;
  }

  .search-panel {
    flex: 0 0 380px;
    position: sticky;
    top: 1.5rem;
  }

  .map-panel {
    flex: 1;
    height: calc(100vh - 3rem);
  }
}
</style>
